import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CheckIcon from "@mui/icons-material/Check";
import AdaptivePopover from "../../common/AdaptivePopover";
import MenuRow from "../../common/MenuRow";
import { hapticSelection } from "../../../lib/haptics";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import dayjs from "dayjs";
import {
  useCompanyEarningsChart,
  EarningsChartData,
} from "./hooks/useCompanyEarnings";
import { useStockCurrency } from "../../../contexts/StockCurrencyContext";
import { getChartColors } from "./constants/chartColors";
import GenericFinancialScatterChart, {
  ScatterDatum,
} from "./components/shared/GenericFinancialScatterChart";
import ChartTooltip from "./components/shared/ChartTooltip";
import { safeToFixed } from "../../../utils/safeToFixed";

// Values are the quarter counts the API fetches; the window includes any
// upcoming announced quarters, so the year framing is approximate by nature.
const RANGE_OPTIONS = [
  { value: "4", label: "1 year", short: "1Y" },
  { value: "8", label: "2 years", short: "2Y" },
  { value: "12", label: "3 years", short: "3Y" },
  { value: "20", label: "5 years", short: "5Y" },
  { value: "40", label: "10 years", short: "10Y" },
  { value: "80", label: "20 years", short: "20Y" },
];
const DEFAULT_RANGE = "12";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

type EpsScatterDatum = ScatterDatum & {
  dateLabel: string;
  estimate: number | null;
  actual: number | null;
  beatStatus: EarningsChartData["beatStatus"];
  beatPercentage: number | null;
};

interface ViewCompanyEarningsProps {
  ticker: string;
  symbol: string;
}

const ViewCompanyEarnings: React.FC<ViewCompanyEarningsProps> = ({
  ticker,
  symbol,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currencySymbol } = useStockCurrency();
  const colors = getChartColors(isDark);

  const isTouch = useTouchLayout();
  // Comfortable tap target on touch; compact beside the chart title on pointer.
  const controlHeight = isTouch ? 34 : 28;

  const [range, setRange] = useState(DEFAULT_RANGE);
  const [showTrendLines, setShowTrendLines] = useState(true);
  const [rangeMenuAnchor, setRangeMenuAnchor] = useState<HTMLElement | null>(null);
  const { chartData, isLoading, error } = useCompanyEarningsChart(
    symbol,
    Number(range)
  );

  const handleRangeSelect = (value: string) => {
    hapticSelection();
    setRange(value);
    setRangeMenuAnchor(null);
  };

  // Estimates render as hollow rings, so the stroke needs more presence than
  // the old filled dot; the reported result still carries the colour.
  const estimateColor = isDark
    ? "rgba(148, 163, 184, 0.9)"
    : "rgba(100, 116, 139, 0.8)";

  const statusColor = (status: EarningsChartData["beatStatus"]) => {
    if (status === "beat") return colors.positive;
    if (status === "miss") return colors.negative;
    return colors.neutral;
  };

  const formatEps = (value: number | null | undefined): string => {
    if (!isFiniteNumber(value)) return "—";
    return `${value < 0 ? "-" : ""}${currencySymbol}${safeToFixed(Math.abs(value), 2)}`;
  };

  const formatSurprise = (value: number | null | undefined): string => {
    if (!isFiniteNumber(value)) return "—";
    return `${value >= 0 ? "+" : ""}${safeToFixed(value, 1)}%`;
  };

  const scatterData = useMemo<EpsScatterDatum[]>(() => {
    const sorted = [...chartData].sort(
      (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    );

    // Report months can collide (rare re-announcements); the " (n)" suffix keeps
    // band keys unique and is stripped from tick labels by the axis renderer.
    const periodCounts = new Map<string, number>();

    return sorted
      .filter((d) => isFiniteNumber(d.epsActual) || isFiniteNumber(d.epsEstimated))
      .map((d) => {
        const basePeriod = dayjs(d.date).format("MMM 'YY");
        const count = periodCounts.get(basePeriod) ?? 0;
        periodCounts.set(basePeriod, count + 1);

        const estimate = isFiniteNumber(d.epsEstimated) ? d.epsEstimated : null;
        const actual = isFiniteNumber(d.epsActual) ? d.epsActual : null;

        return {
          period: count > 0 ? `${basePeriod} (${count + 1})` : basePeriod,
          dateLabel: dayjs(d.date).format("MMM D, YYYY"),
          value: actual,
          color: statusColor(d.beatStatus),
          refValue: estimate,
          refColor: estimateColor,
          estimate,
          actual,
          beatStatus: d.beatStatus,
          beatPercentage: isFiniteNumber(d.beatPercentage) ? d.beatPercentage : null,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, estimateColor, colors.positive, colors.negative, colors.neutral]);

  const stats = useMemo(() => {
    const reported = scatterData.filter(
      (d) =>
        d.beatStatus === "beat" ||
        d.beatStatus === "miss" ||
        d.beatStatus === "inline"
    );
    const beats = reported.filter((d) => d.beatStatus === "beat").length;
    const misses = reported.filter((d) => d.beatStatus === "miss").length;
    const inline = reported.filter((d) => d.beatStatus === "inline").length;
    // Median, not mean: surprise percentages explode when an estimate sits
    // near zero (one -900% quarter should not define the headline stat).
    const surprises = reported
      .map((d) => d.beatPercentage)
      .filter(isFiniteNumber)
      .sort((a, b) => a - b);
    const mid = Math.floor(surprises.length / 2);
    const medianSurprise =
      surprises.length === 0
        ? null
        : surprises.length % 2 === 1
          ? surprises[mid]
          : (surprises[mid - 1] + surprises[mid]) / 2;

    return { beats, misses, inline, medianSurprise };
  }, [scatterData]);

  const hairline = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";

  const legendItems = [
    { label: "Beat", color: colors.positive, size: 10, hollow: false },
    { label: "Miss", color: colors.negative, size: 10, hollow: false },
    { label: "Inline", color: colors.neutral, size: 10, hollow: false },
    { label: "Estimate", color: estimateColor, size: 9, hollow: true },
  ];

  const statItems = [
    { label: "Beats", value: `${stats.beats}`, color: colors.positive },
    { label: "Misses", value: `${stats.misses}`, color: colors.negative },
    { label: "Inline", value: `${stats.inline}`, color: colors.neutral },
    ...(stats.medianSurprise !== null
      ? [
          {
            label: "Med Surprise",
            value: formatSurprise(stats.medianSurprise),
            color: stats.medianSurprise >= 0 ? colors.positive : colors.negative,
          },
        ]
      : []),
  ];

  const footer = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1.5,
        mt: 2,
        pt: 1.75,
        borderTop: `1px solid ${hairline}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        {legendItems.map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: item.size,
                height: item.size,
                borderRadius: "50%",
                backgroundColor: item.hollow ? "transparent" : item.color,
                border: item.hollow ? `1.5px solid ${item.color}` : "none",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "0.72rem", color: theme.palette.text.secondary }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        {statItems.map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: theme.palette.text.disabled,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: item.color,
              }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      data-tour="earnings-eps-chart"
      sx={{
        // Same card surface as the ViewTicker tabs — lifts the card off the
        // page background, which is background.default and otherwise identical.
        backgroundColor: theme.palette.background.surface,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <GenericFinancialScatterChart
        title="EPS — Estimate vs Actual"
        ticker={ticker}
        height={isMobile ? 320 : 440}
        renderCard={false}
        showFullscreen
        isLoading={isLoading}
        error={error ?? null}
        chartData={scatterData}
        yAxisLegend="EPS"
        yAxisFormat={formatEps}
        leftMargin={isMobile ? 55 : 80}
        showConnectors={false}
        showTrendLines={showTrendLines}
        headerActions={
          <>
            <ButtonBase
              onClick={(e) => setRangeMenuAnchor(e.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={rangeMenuAnchor !== null}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                height: controlHeight,
                px: 1.25,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: theme.palette.text.secondary,
                WebkitTapHighlightColor: "transparent",
                transition: "background-color 0.15s ease, color 0.15s ease",
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255, 255, 255, 0.04)"
                    : "rgba(0, 0, 0, 0.03)",
                  color: theme.palette.text.primary,
                },
                "&:active": { transform: "scale(0.96)" },
              }}
            >
              {RANGE_OPTIONS.find((o) => o.value === range)?.short ?? `${range}Q`}
              <ExpandMoreRoundedIcon sx={{ fontSize: 16 }} />
            </ButtonBase>
            <Tooltip title="Trend lines">
              <ButtonBase
                onClick={() => setShowTrendLines((prev) => !prev)}
                aria-pressed={showTrendLines}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: controlHeight,
                  width: controlHeight + 6,
                  borderRadius: 2,
                  border: `1px solid ${
                    showTrendLines ? "rgba(34, 197, 94, 0.5)" : theme.palette.divider
                  }`,
                  backgroundColor: showTrendLines
                    ? "rgba(34, 197, 94, 0.1)"
                    : "transparent",
                  color: showTrendLines ? "#22c55e" : theme.palette.text.secondary,
                  WebkitTapHighlightColor: "transparent",
                  transition:
                    "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: showTrendLines
                      ? "rgba(34, 197, 94, 0.16)"
                      : isDark
                        ? "rgba(255, 255, 255, 0.04)"
                        : "rgba(0, 0, 0, 0.03)",
                    color: showTrendLines ? "#22c55e" : theme.palette.text.primary,
                  },
                  "&:active": { transform: "scale(0.96)" },
                }}
              >
                <ShowChartIcon sx={{ fontSize: 18 }} />
              </ButtonBase>
            </Tooltip>
          </>
        }
        keepHeaderWhenEmpty
        noDataTitle="No Earnings Data Available"
        noDataDescription="Estimate and reported EPS history is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
        tooltipContent={(datum) => {
          const d = datum as EpsScatterDatum;
          const isUpcoming = d.beatStatus === "upcoming";

          const rows = [
            ...(d.estimate !== null
              ? [{ label: "Estimate", value: formatEps(d.estimate), color: estimateColor }]
              : []),
            ...(d.actual !== null
              ? [
                  {
                    label: "Actual",
                    value: formatEps(d.actual),
                    color: statusColor(d.beatStatus),
                  },
                ]
              : []),
            ...(d.beatPercentage !== null
              ? [{ label: "Surprise", value: formatSurprise(d.beatPercentage) }]
              : []),
          ];

          return (
            <ChartTooltip
              header={isUpcoming ? `${d.dateLabel} — Upcoming` : d.dateLabel}
              headerColor={isUpcoming ? undefined : statusColor(d.beatStatus)}
              rows={rows}
            />
          );
        }}
        belowChartContent={footer}
      />

      <AdaptivePopover
        open={rangeMenuAnchor !== null}
        anchorEl={rangeMenuAnchor}
        onClose={() => setRangeMenuAnchor(null)}
        title="History range"
      >
        <Box role="menu">
          {RANGE_OPTIONS.map((option) => (
            <MenuRow
              key={option.value}
              label={option.label}
              selected={option.value === range}
              trailing={
                option.value === range ? (
                  <CheckIcon fontSize="small" sx={{ color: "#22c55e" }} />
                ) : null
              }
              onClick={() => handleRangeSelect(option.value)}
            />
          ))}
        </Box>
      </AdaptivePopover>
    </Box>
  );
};

export default ViewCompanyEarnings;
